import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Compass, Loader2, Sparkles, Target, TrendingUp, Users } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { findNiches } from "@/lib/api";

const AI_BG = "https://static.prod-images.emergentagent.com/jobs/4ca59440-3756-4a33-9153-174842ff835f/images/1a713da9deb109b37ab12042e96910112909eec73e03ac7db18e18cd23cad386.png";
const EMPTY = "https://static.prod-images.emergentagent.com/jobs/4ca59440-3756-4a33-9153-174842ff835f/images/f3df7140de241ba362f5b7d787522201e3bdc4c710a971bcf75cccc27d55c611.png";

const BUDGETS = ["niedrig", "mittel", "hoch"];
const COMP = {
  niedrig: "bg-emerald-50 text-emerald-600",
  mittel: "bg-amber-50 text-amber-600",
  hoch: "bg-rose-50 text-rose-600",
};

export default function NicheFinder() {
  const [interests, setInterests] = useState("");
  const [budget, setBudget] = useState("niedrig");
  const [audience, setAudience] = useState("Deutschland, Schnäppchenjäger");
  const [loading, setLoading] = useState(false);
  const [niches, setNiches] = useState(null);

  const handleFind = async () => {
    setLoading(true);
    try {
      const res = await findNiches({ interests, budget, audience });
      setNiches(res.niches || []);
      toast.success(`${(res.niches || []).length} Nischen gefunden`);
    } catch (e) {
      toast.error("Nischen-Analyse fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="niches-page">
      <PageHeader title="Nischen-Finder" subtitle="Die KI findet profitable, nicht übersättigte Nischen für dich" />

      <div className="px-6 md:px-10 py-8">
        {/* Hero form */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl border border-black/5 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden mb-8"
        >
          <img src={AI_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none" />
          <div className="relative p-6 md:p-8">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#A259FF]/10 px-3 py-1 mb-4">
              <Compass className="h-3.5 w-3.5 text-[#8928E2]" />
              <span className="text-xs font-semibold ai-gradient-text">KI Markt-Stratege</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end max-w-4xl">
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Interessen (optional)</label>
                <Input value={interests} onChange={(e) => setInterests(e.target.value)} placeholder="z.B. Gaming, Haushalt..." className="rounded-xl bg-white/80 border-black/5" data-testid="niche-interests" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Startbudget</label>
                <Select value={budget} onValueChange={setBudget}>
                  <SelectTrigger className="rounded-xl bg-white/80 border-black/5" data-testid="niche-budget"><SelectValue /></SelectTrigger>
                  <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1D1D1F] block mb-2">Zielgruppe</label>
                <Input value={audience} onChange={(e) => setAudience(e.target.value)} className="rounded-xl bg-white/80 border-black/5" data-testid="niche-audience" />
              </div>
            </div>
            <Button
              onClick={handleFind}
              disabled={loading}
              className="mt-5 rounded-full ai-gradient-bg text-white hover:opacity-90 active:scale-95 transition-all px-8"
              data-testid="niche-find-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Nischen finden
            </Button>
          </div>
        </motion.div>

        {/* Results */}
        {!niches ? (
          <div className="text-center py-16">
            <img src={EMPTY} alt="" className="max-h-44 mx-auto mb-4" />
            <p className="font-heading font-semibold text-[#1D1D1F]">Noch keine Analyse</p>
            <p className="text-sm text-[#86868B] mt-1">Starte die KI-Analyse, um profitable Nischen zu entdecken.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="niche-results">
            {niches.map((n, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:-translate-y-[2px]"
                data-testid={`niche-card-${i}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-heading text-lg font-bold text-[#1D1D1F] leading-tight">{n.name}</h3>
                  <span className={`text-[11px] font-medium rounded-full px-2.5 py-0.5 shrink-0 ${COMP[n.competition] || "bg-black/5 text-[#86868B]"}`}>
                    {n.competition}
                  </span>
                </div>
                <p className="text-sm text-[#86868B] leading-relaxed mb-4">{n.why}</p>

                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-[#1D1D1F] font-medium inline-flex items-center gap-1">
                      <TrendingUp className="h-3.5 w-3.5 text-[#34C759]" /> Profit-Potenzial
                    </span>
                    <span className="ai-gradient-text font-semibold">{n.profit_potential}/100</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#F5F5F7] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${n.profit_potential}%` }}
                      transition={{ delay: 0.2 + i * 0.05, type: "spring", stiffness: 120, damping: 20 }}
                      className="h-full rounded-full ai-gradient-bg"
                    />
                  </div>
                </div>

                {n.target_audience && (
                  <div className="flex items-start gap-2 text-xs text-[#86868B] mb-3">
                    <Users className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{n.target_audience}</span>
                  </div>
                )}

                {Array.isArray(n.example_products) && n.example_products.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-[#86868B] uppercase tracking-wide mb-2 inline-flex items-center gap-1">
                      <Target className="h-3 w-3" /> Beispiel-Produkte
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {n.example_products.map((p, j) => (
                        <span key={j} className="text-xs text-[#1D1D1F] bg-[#F5F5F7] rounded-full px-2.5 py-1">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
